import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { useAppStore } from '@src/app/context'
import assertNever from '@src/lib/assertNever'
import type { VoiceAnalyticsCallQualityPayload } from '@src/service/analytics/VoiceAnalyticsStore'
import { useService } from '@src/service/context'

import CallQualityWizard from './CallQualityWizard'
import type {
  CallQualityWizardFeedback,
  CallQualityWizardState,
  FeedbackEvent,
} from './CallQualityWizard'
import type { EffectiveConnectionType } from './NetworkInformationUiStore'

interface ConnectedCallQualityWizardProps {
  call: VoiceAnalyticsCallQualityPayload
  endedAt: number
  networkInformation: {
    connectionType: string
    effectiveConnectionType: EffectiveConnectionType
  }
  appVersion: string
}

function ConnectedCallQualityWizard({
  call,
  endedAt,
  networkInformation,
  appVersion,
}: ConnectedCallQualityWizardProps) {
  const { voice, analytics } = useService()
  const app = useAppStore()
  const [isFeedbackSubmitted, setFeedbackSubmitted] = useState(false)

  const handleCallQualityFeedbackComplete = async (
    state: CallQualityWizardState,
    payload: CallQualityWizardFeedback,
  ) => {
    const isTerminatingState =
      payload.feedback === 1 || payload.callQuality.issues.includes('other')

    if (state === 'close') {
      if (!isFeedbackSubmitted && payload.feedback !== -1) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
        voice.submitCallQualityFeedback(payload)
      }
      voice.setCallQualityFeedbackRequest(null)
      return
    }

    if (isTerminatingState) {
      voice.setCallQualityFeedbackRequest(null)
      app.toast.show({ message: 'Thanks for your feedback!' })
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    voice.submitCallQualityFeedback(payload)
    setFeedbackSubmitted(true)
  }

  const handleCallQualityFeedbackEvent = (
    event: FeedbackEvent,
    payload: CallQualityWizardFeedback,
  ) => {
    switch (event) {
      case 'call_quality_rated':
        return analytics.voice.callQualityRated({
          rating: payload.feedback === 1 ? 'good' : 'bad',
          call,
        })
      case 'call_quality_reason_given':
        return analytics.voice.callQualityIssueGiven({
          issue: payload.callQuality.issues?.[0] ?? 'other',
          issueText: payload.userText,
          call,
        })
      default:
        assertNever(event, 'Unhandled call quality feedback modal event')
    }
  }

  return (
    <CallQualityWizard
      audioInterface={'unknown'}
      callId={call.id}
      callDuration={endedAt}
      onComplete={handleCallQualityFeedbackComplete}
      onEvent={handleCallQualityFeedbackEvent}
      onMount={() => {
        analytics.voice.callQualityFeedbackShown({
          call,
        })
      }}
      networkInformation={networkInformation}
      finalCallMetrics={call.metrics}
      appVersion={appVersion}
    />
  )
}

export default observer(ConnectedCallQualityWizard)
